import React from "react";
import { localePath } from "@/i18n/localePath";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ScrollReveal } from "./ScrollEffects";
import { ArrowRight, Shield, Zap, Heart } from "lucide-react";

export const CTASection: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  return (
    <section
      id="cta"
      className="py-20 sm:py-32 px-4 relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0" style={{
        background: "radial-gradient(ellipse 80% 70% at 50% 50%, hsl(35 80% 92% / 0.5) 0%, hsl(var(--background)) 70%)",
      }} />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Floating elements */}
      {[
        { emoji: "🐾", x: "8%", y: "15%", delay: 0, dur: 18 },
        { emoji: "❤️", x: "88%", y: "25%", delay: 3, dur: 16 },
        { emoji: "🐕", x: "78%", y: "72%", delay: 5, dur: 20 },
        { emoji: "💛", x: "15%", y: "78%", delay: 2, dur: 14 },
        { emoji: "🐾", x: "50%", y: "10%", delay: 4, dur: 22 },
        { emoji: "✨", x: "92%", y: "55%", delay: 1, dur: 17 },
      ].map((f, i) => (
        <motion.span
          key={i}
          className="absolute text-lg pointer-events-none select-none"
          style={{ left: f.x, top: f.y, opacity: 0.08 }}
          animate={{ y: [0, -35, 0], rotate: [0, 10, -10, 0], opacity: [0.06, 0.15, 0.06] }}
          transition={{ duration: f.dur, delay: f.delay, repeat: Infinity, ease: "easeInOut" }}
        >
          {f.emoji}
        </motion.span>
      ))}

      <ScrollReveal className="max-w-2xl mx-auto text-center relative z-10">
        <motion.div
          className="text-7xl sm:text-8xl mb-8"
          initial={{ scale: 0, rotate: -30 }}
          whileInView={{ scale: 1, rotate: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 180, damping: 12, delay: 0.2 }}
        >
          <motion.span
            className="inline-block"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            🐕
          </motion.span>
        </motion.div>

        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground mb-5 leading-tight">
          {t('cta.title')}
        </h2>
        <p className="text-muted-foreground text-base sm:text-lg mb-8 max-w-md mx-auto leading-relaxed">
          {t('cta.subtitle')}
        </p>

        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Button
            onClick={() => navigate(localePath("/auth", i18n.language))}
            size="lg"
            className="rounded-2xl h-16 px-14 text-lg font-bold group relative overflow-hidden"
            style={{ boxShadow: "0 8px 40px hsl(var(--primary) / 0.25), 0 0 80px hsl(var(--primary) / 0.08)" }}
          >
            {/* Shimmer effect */}
            <motion.div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: "linear-gradient(105deg, transparent 20%, hsla(0,0%,100%,0.2) 45%, hsla(0,0%,100%,0.3) 50%, hsla(0,0%,100%,0.2) 55%, transparent 80%)",
                backgroundSize: "200% 100%",
              }}
              animate={{ backgroundPosition: ["-100% 0", "200% 0"] }}
              transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1 }}
            />
            <span className="relative z-10 flex items-center gap-2">
              {t('cta.button')}
              <motion.span
                className="inline-block"
                animate={{ x: [0, 4, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ArrowRight size={18} />
              </motion.span>
            </span>
          </Button>
        </motion.div>

        {/* Trust badges */}
        <motion.div
          className="flex items-center justify-center gap-6 sm:gap-8 mt-8 text-muted-foreground/40"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          {[
            { icon: Shield, label: "No credit card" },
            { icon: Zap, label: "Setup in 2 min" },
            { icon: Heart, label: "Free forever" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs font-medium">
              <item.icon size={12} />
              <span>{item.label}</span>
            </div>
          ))}
        </motion.div>

        <motion.p
          className="text-xs text-muted-foreground/40 mt-4 font-medium"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7 }}
        >
          {(t as any)('cta.social_proof', { count: '2,400' })}
        </motion.p>
      </ScrollReveal>
    </section>
  );
};
